import { Box, Center } from '@chakra-ui/react';
import type { Mention } from '@fluenceur/types';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { MentionThumbnailPreviewSurface } from './MentionThumbnailPreviewSurface';
import { MentionVideoPosterFrame } from './MentionVideoPosterFrame';
import PlayerVideoButton from './PlayerVideoButton';

const MotionCenter = motion(Center);

interface MentionVideoThumbnailProps {
  videoUrl: string;
  isHovered?: boolean;
  mention: Mention;
  openOnClick?: boolean;
  /** The video failed to load (an expired signed URL): the caller repairs it. */
  onError?: () => void;
}

interface VideoProgress {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
}

/**
 * Component for displaying a video thumbnail with play button overlay.
 * Shows a playing video preview when hovered.
 */
const MentionVideoThumbnail: React.FC<MentionVideoThumbnailProps> = ({
  videoUrl,
  isHovered,
  mention,
  openOnClick = true,
  onError,
}) => {
  const [videoProgress, setVideoProgress] = useState<VideoProgress | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  const handleVideoStart = () => {
    // Video has started playing - now it's safe to show
    setVideoLoaded(true);
  };

  // Reset state when hover ends
  useEffect(() => {
    if (!isHovered) {
      setVideoLoaded(false);
      setVideoProgress(null);
      setVideoDuration(null);
    }
  }, [isHovered]);

  return (
    <MentionThumbnailPreviewSurface mentionId={mention.id} openOnClick={openOnClick}>
      {/* ReactPlayer - Always load when hovered */}
      {isHovered && (
        <ReactPlayer
          key={videoUrl}
          url={videoUrl}
          width="100%"
          height="100%"
          playing={true}
          muted
          loop
          onProgress={setVideoProgress}
          onDuration={setVideoDuration}
          onStart={handleVideoStart}
          progressInterval={100}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: videoLoaded ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
          }}
          config={{
            file: {
              attributes: {
                style: {
                  objectFit: 'cover',
                  outline: 'none',
                  width: '100%',
                  height: '100%',
                },
              },
            },
          }}
        />
      )}

      {/* HTML5 video thumbnail - Always show until video is loaded */}
      <MentionVideoPosterFrame videoUrl={videoUrl} faded={videoLoaded} onError={onError} />

      {/* Gradient overlay */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient="linear(to-b, transparent 60%, blackAlpha.200 100%)"
        pointerEvents="none"
      />

      {/* Play button - only show when video not loaded */}
      <AnimatePresence>
        {!videoLoaded && (
          <MotionCenter
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            initial={{ opacity: 1, scale: 1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <PlayerVideoButton size="md" />
          </MotionCenter>
        )}
      </AnimatePresence>

      {/* Progress bar */}
      {videoLoaded && videoDuration && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          height="4px"
          bg="whiteAlpha.300"
          zIndex={1}
          opacity={videoProgress ? 1 : 0}
          transition="opacity 0.3s ease-in-out"
        >
          <Box
            height="100%"
            bg="white"
            width={`${videoProgress ? (videoProgress.played * 100).toFixed(1) : 0}%`}
            transition="width 0.15s ease-out"
            style={{
              willChange: 'width',
            }}
          />
        </Box>
      )}
    </MentionThumbnailPreviewSurface>
  );
};

export default MentionVideoThumbnail;
